/**
 * Whiteboard domain types — mirrors the API's `whiteboard-rows` /
 * `whiteboard-cells` TypeBox schemas (packages/api/src/services/organization/
 * whiteboard). A ROW is a lane on one of the two boards; a CELL is that lane's
 * note for ONE week, keyed by the week's Monday as 'YYYY-MM-DD'.
 */

export type WhiteboardBoard = "delivery" | "sales";

/** The whiteboard's four-status language (+ hold for parked sales lanes). */
export type WhiteboardStatus =
  | "in_process"
  | "done"
  | "needs_schedule"
  | "late"
  | "hold";

export interface WhiteboardRowRecord {
  id: string;
  board: WhiteboardBoard;
  /** Client 360 tie — null for internal lanes / not-yet-client prospects. */
  clientId?: string | null;
  title: string;
  resource?: string | null;
  workstream?: string | null;
  notes?: string | null;
  /** Lane-level status: the Sales Queue's DEAL status, a key of the admin-edited
   *  taxonomy (`whiteboard-statuses`; see {@link laneStatusMeta}). */
  status?: WhiteboardStatus | string | null;
  sortOrder: number;
  /** The lane's target delivery or go-live date ('YYYY-MM-DD'), for Slipping. */
  targetDate?: string | null;
  /** What `targetDate` is: "Go-live", "Delivery"… */
  targetLabel?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

export interface WhiteboardCellRecord {
  id: string;
  rowId: string;
  /** The week's Monday, 'YYYY-MM-DD' (the API normalizes any date sent). */
  weekOf: string;
  status: WhiteboardStatus | string;
  note?: string | null;
  dueDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

/**
 * Display metadata per status — the EXACT fills the physical/Excel whiteboard
 * used (its legend), paired with the app's standard strong colors for borders
 * and text. Keeping the team's color language is the point: the board should
 * read like the whiteboard, not like a new tool.
 */
export const WHITEBOARD_STATUS_META: Record<
  string,
  { label: string; fill: string; border: string; text: string }
> = {
  in_process: { label: "In process", fill: "#cfe2f3", border: "#9fc5e8", text: "#0d47a1" },
  done: { label: "Done", fill: "#d9ead3", border: "#93c47d", text: "#1b5e20" },
  needs_schedule: {
    label: "Needs assignment / schedule",
    fill: "#fce5cd",
    border: "#f6b26b",
    text: "#b45309",
  },
  late: { label: "Deadline / late", fill: "#f4cccc", border: "#e06666", text: "#c62828" },
  hold: { label: "On hold", fill: "#eeeeee", border: "#bdbdbd", text: "#616161" },
};

export const statusMeta = (status?: string | null) =>
  WHITEBOARD_STATUS_META[status ?? ""] ?? WHITEBOARD_STATUS_META.in_process;

/** Statuses offered by the cell editor (hold is a lane-level state). */
export const CELL_STATUSES: WhiteboardStatus[] = [
  "in_process",
  "done",
  "needs_schedule",
  "late",
];

/**
 * The Filters button's "This week" choice: a cell status, or `none` for a lane
 * with nothing scheduled this week. "This week" is the calendar week (the
 * column marked "This week"), the same week the legend counts.
 */
export type WeekStatusFilter = WhiteboardStatus | "none";

export const WEEK_STATUS_FILTER_OPTIONS: ReadonlyArray<{
  value: WeekStatusFilter;
  label: string;
}> = [
  ...CELL_STATUSES.map((status) => ({ value: status, label: statusMeta(status).label })),
  { value: "none", label: "Nothing scheduled" },
];

// ---- Week math (mirrors the API's week.ts; UTC so timezones can't shift a
// cell into the neighboring week) ------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

const parseUtc = (value: string): number => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
};

const toIsoDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** The Monday of the week containing `value` ('YYYY-MM-DD'), or "" on junk. */
export function weekStartOf(value: string): string {
  const ms = parseUtc(value);
  if (Number.isNaN(ms)) return "";
  const offset = (new Date(ms).getUTCDay() + 6) % 7;
  return toIsoDate(ms - offset * DAY_MS);
}

/** This week's Monday (local clock date, UTC week math). */
export function currentWeekStart(now: Date = new Date()): string {
  const local = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
  return weekStartOf(local);
}

/** Shift a week key by N weeks (negative for past). */
export function addWeeks(weekOf: string, weeks: number): string {
  const ms = parseUtc(weekOf);
  return Number.isNaN(ms) ? weekOf : toIsoDate(ms + weeks * 7 * DAY_MS);
}

/**
 * The one formatter weekLabel uses, and its answers by day. `toLocaleDateString`
 * builds a new Intl formatter on every call, and the board labels hundreds of
 * days (column headers, target dates, due days, blockers' since-when): that was
 * the most expensive function in a profile of the board's render (40 lanes x 12
 * weeks). Built on first use, so the runtime's locale still decides the words.
 */
let dayFormat: Intl.DateTimeFormat | null = null;
const dayLabels = new Map<string, string>();

/** "Aug 17" — the board's column label for a week key (or any day). */
export function weekLabel(weekOf: string): string {
  const known = dayLabels.get(weekOf);
  if (known !== undefined) return known;
  const ms = parseUtc(weekOf);
  if (Number.isNaN(ms)) return weekOf;
  dayFormat ??= new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  const label = dayFormat.format(new Date(ms));
  // shortcut: unbounded, but keyed by calendar day — a session sees a few hundred.
  dayLabels.set(weekOf, label);
  return label;
}

// ---- Weekly entries (#3383) ------------------------------------------------
//
// Mirrors the API's `whiteboard-items` (packages/api/src/services/organization/
// whiteboard). The lists below are held equal to the API's vocabulary file by
// whiteboardVocabularyParity.test.ts.

/** A lane's weekly goal, milestone or blocker. Weekly by design (2026-10-20). */
export const WHITEBOARD_ITEM_KINDS = ["goal", "milestone", "blocker"] as const;
export type WhiteboardItemKind = (typeof WHITEBOARD_ITEM_KINDS)[number];

/** `open` until someone marks it done or drops it. */
export const WHITEBOARD_ITEM_STATUSES = ["open", "done", "dropped"] as const;
export type WhiteboardItemStatus = (typeof WHITEBOARD_ITEM_STATUSES)[number];

export interface WhiteboardItemRecord {
  id: string;
  rowId: string;
  kind: WhiteboardItemKind | string;
  title: string;
  note?: string | null;
  /** Whose it is; for a blocker, who owns clearing it. */
  ownerUserId: number;
  /** The week's Monday, 'YYYY-MM-DD'. */
  weekOf: string;
  /** Optional day it is due; without one, the end of its week. */
  dueDate?: string | null;
  status: WhiteboardItemStatus | string;
  resolvedAt?: string | null;
  resolvedBy?: number | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

export const ITEM_KIND_META: Record<WhiteboardItemKind, { label: string; plural: string }> = {
  goal: { label: "Goal", plural: "Goals" },
  milestone: { label: "Milestone", plural: "Milestones" },
  blocker: { label: "Blocker", plural: "Blockers" },
};

export const itemKindLabel = (kind: string): string =>
  ITEM_KIND_META[kind as WhiteboardItemKind]?.label ?? kind;

// ---- The deal-status taxonomy (#3080 / #2662) --------------------------------

export const LANE_STATUS_CATEGORIES = ["active", "on_hold", "closed"] as const;
export type LaneStatusCategory = (typeof LANE_STATUS_CATEGORIES)[number];

export const LANE_STATUS_CATEGORY_LABEL: Record<LaneStatusCategory, string> = {
  active: "Active",
  on_hold: "On hold",
  closed: "Closed",
};

/**
 * The colours a deal status may take: the board's four (blue, green, orange,
 * red), the grey of a parked lane, and three for a status that needs its own.
 * The first five are exactly the whiteboard legend's fills (WHITEBOARD_STATUS_META).
 */
export const LANE_STATUS_COLORS = [
  "blue",
  "green",
  "orange",
  "red",
  "grey",
  "purple",
  "teal",
  "yellow",
] as const;
export type LaneStatusColor = (typeof LANE_STATUS_COLORS)[number];

export const LANE_STATUS_COLOR_META: Record<
  LaneStatusColor,
  { name: string; fill: string; border: string; text: string }
> = {
  blue: { name: "Blue (in process)", ...pick(WHITEBOARD_STATUS_META.in_process) },
  green: { name: "Green (done)", ...pick(WHITEBOARD_STATUS_META.done) },
  orange: { name: "Orange (needs assignment)", ...pick(WHITEBOARD_STATUS_META.needs_schedule) },
  red: { name: "Red (late)", ...pick(WHITEBOARD_STATUS_META.late) },
  grey: { name: "Grey (on hold)", ...pick(WHITEBOARD_STATUS_META.hold) },
  purple: { name: "Purple", fill: "#e4dcf3", border: "#b4a7d6", text: "#4a148c" },
  teal: { name: "Teal", fill: "#d0ebe8", border: "#76a5af", text: "#00695c" },
  yellow: { name: "Yellow", fill: "#fff2cc", border: "#f1c232", text: "#7f6000" },
};

function pick(meta: { fill: string; border: string; text: string }) {
  return { fill: meta.fill, border: meta.border, text: meta.text };
}

/** One deal status as `whiteboard-statuses` returns it. */
export interface WhiteboardStatusRecord {
  id: string;
  /** What a lane stores. Never changes. */
  statusKey: string;
  label: string;
  color: LaneStatusColor | string;
  category: LaneStatusCategory | string;
  sortOrder: number;
  /** Set = retired: out of the pickers, still a lane's label. */
  deletedAt?: string | null;
}

export interface LaneStatusMeta {
  label: string;
  fill: string;
  border: string;
  text: string;
  category: LaneStatusCategory | null;
  retired: boolean;
}

/**
 * What a lane's deal status looks like, from the taxonomy. Before the
 * taxonomy has loaded (or on an API that has not migrated) a key it does not
 * know falls back to the board's original five; a key nobody knows shows as
 * itself, in grey, never as "In process" (what statusMeta's fallback would
 * claim).
 */
export function laneStatusMeta(
  statuses: readonly WhiteboardStatusRecord[],
  key: string | null | undefined
): LaneStatusMeta | null {
  if (!key) return null;
  const found =
    statuses.find((s) => s.statusKey === key) ??
    statuses.find((s) => s.statusKey.toLowerCase() === key.trim().toLowerCase());
  if (found) {
    const colour = LANE_STATUS_COLOR_META[found.color as LaneStatusColor] ?? LANE_STATUS_COLOR_META.grey;
    return {
      label: found.label,
      fill: colour.fill,
      border: colour.border,
      text: colour.text,
      category: (LANE_STATUS_CATEGORIES as readonly string[]).includes(found.category)
        ? (found.category as LaneStatusCategory)
        : null,
      retired: !!found.deletedAt,
    };
  }
  const legacy = WHITEBOARD_STATUS_META[key];
  if (legacy) {
    return {
      ...legacy,
      category: key === "done" ? "closed" : key === "hold" ? "on_hold" : "active",
      retired: false,
    };
  }
  return { label: key, ...LANE_STATUS_COLOR_META.grey, category: null, retired: false };
}

// ---- Accepted work, automatically (#2516) ----------------------------------

export type LaneWorkState = "past_due" | "completed" | "in_progress";

/** One accepted action item on a lane, as `whiteboard-lane-work` returns it. */
export interface LaneWorkItem {
  id: number;
  title: string;
  state: LaneWorkState;
  dueDate: string | null;
  daysPastDue: number | null;
  completedAt: string | null;
  assigneeName: string | null;
  workItemId: string | null;
  workItemUrl: string | null;
  prUrl: string | null;
  governedSlug: string | null;
}

/** One client's accepted work. Counts are over all of it; `items` is capped. */
export interface LaneWork {
  clientId: string;
  /** Accepted and not completed, past due included. */
  open: number;
  pastDue: number;
  completedThisWeek: number;
  items: LaneWorkItem[];
  more: number;
}

/** The `whiteboard-lane-work` find envelope. */
export interface LaneWorkResponse {
  total: number;
  data: LaneWork[];
  today: string;
  weekStart: string;
  timeZone: string;
  truncated: boolean;
}
